/* /ess/letters — request an HR letter + own letter history (prototype pgEssLetters). */
import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../../data";
import { ApiError } from "../../data/provider";
import { useCreateLetter, useLetters } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import { Button, Card, Chip, EmptyState, Field, Icon, Input, cx, errMsg, useToast } from "../../ui";
import { NoLink } from "./EssHome";
import { useEmployeeId } from "./essLib";

export const LETTER_KINDS: [string, string, string][] = [
  ["Salary certificate", "banknote", "Al Rajhi Bank"],
  ["Experience letter", "award", "To whom it may concern"],
  ["No objection certificate", "shield", ""],
  ["Salary transfer letter", "wallet", ""],
  ["Embassy letter", "globe", ""],
];

function Body({ empId }: { empId: number }) {
  const { t, L, lang } = useI18n();
  const toast = useToast();
  const nav = useNavigate();
  const data = useData();
  const list = useLetters();
  const create = useCreateLetter();
  const toId = useId();
  const [kind, setKind] = useState(LETTER_KINDS[0][0]);
  const [to, setTo] = useState(LETTER_KINDS[0][2]);
  const [err, setErr] = useState<string | undefined>();
  const mine = (list.data || []).filter((l) => l.employeeId === empId);
  const pickKind = (k: string) => {
    const prev = LETTER_KINDS.find((x) => x[0] === kind);
    const next = LETTER_KINDS.find((x) => x[0] === k);
    setKind(k);
    if (!to || to === prev?.[2]) setTo(next?.[2] ?? "");
    setErr(undefined);
  };
  const submit = async () => {
    if (!to.trim()) { setErr(L("Who should the letter be addressed to?", "إلى من يُوجه الخطاب؟")); return; }
    try {
      await create.mutateAsync({ employeeId: empId, kind, to: to.trim() });
      toast(L("Request sent to HR", "تم إرسال الطلب إلى الموارد البشرية"));
      nav("/ess");
    } catch (e) {
      if (e instanceof ApiError && e.fields?.to) setErr(e.fields.to);
      toast(errMsg(e), "bad");
    }
  };
  return (
    <div className="ess" data-testid="ess-letters">
      <div className="ph"><div><h1>{t("Request letter")}</h1><p>{L("HR usually issues letters within one working day", "تصدر الموارد البشرية الخطابات عادةً خلال يوم عمل")}</p></div></div>
      <div className="stack" role="radiogroup" aria-label={L("Letter type", "نوع الخطاب")}>
        {LETTER_KINDS.map(([k, ic]) => (
          <button key={k} type="button" role="radio" aria-checked={kind === k} className={cx("tpl", kind === k && "on")} onClick={() => pickKind(k)} data-testid={"kind-" + k.replace(/\s+/g, "-")}>
            <span className="row nw"><span className="kpi-ico"><Icon name={ic} /></span><b>{t(k)}</b></span>
          </button>
        ))}
        <Field label={L("Addressed to", "موجه إلى")} htmlFor={toId} err={err}>
          <Input id={toId} value={to} bad={!!err} onChange={(e) => { setTo(e.target.value); setErr(undefined); }} data-testid="letter-to" />
        </Field>
      </div>
      <div className="actionbar"><span className="spacer" /><Button variant="pri" icon="check" loading={create.isPending} onClick={submit} data-testid="letter-submit">{t("Submit")}</Button></div>
      <div className="mt">
        <Card title={L("My letters", "خطاباتي")} flush testId="my-letters">
          {mine.length === 0 ? <EmptyState title={L("No letters yet", "لا توجد خطابات بعد")} desc={L("Requested letters and their status appear here.", "تظهر هنا الخطابات المطلوبة وحالتها.")} /> : mine.map((l) => (
            <div key={l.id} className="list-row" style={{ paddingInline: 16 }} data-testid={"letter-" + l.id}>
              <span className="kpi-ico gold"><Icon name="mail" /></span>
              <div className="grow"><div>{t(l.kind)}</div><div className="xs muted">{l.to} · {gDate(l.date, lang)}</div></div>
              <Chip status={l.status} />
              {l.status === "Issued" && l.fileKey && <a className="btn ghost sm" href={data.fileUrl(l.fileKey)} target="_blank" rel="noreferrer" data-testid={"letter-dl-" + l.id}><Icon name="download" /><span>{L("Download", "تنزيل")}</span></a>}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

export default function EssLetters() {
  const id = useEmployeeId();
  return id == null ? <NoLink /> : <Body empId={id} />;
}
